import { AssignmentStatusType, AssignmentType } from '../type';

export type AssignmentRecitationItem = {
  id: number;
  book: string;
  lesson: string;
  code: string;
  title: string;
  type: AssignmentType;
  link: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type AssignmentClassItem = {
  id: number;
  classId: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

export type AssignmentStudentItem = {
  id: number;
  classInfos: { classId: string; classGroup: { id: number } }[];
  name: string;
  uid: string;
  phone: string;
  email: string;
  birthday: string;
  createdAt: string;
};

export type VoucherBalanceItem = {
  type: AssignmentType;
  remaining: number;
};

/**
 * -----------------------------------------------------------------
 */

export type ClassGroupSummaryItem = {
  classGroupId: number;
  classGroupName: string;
  completionRate: number;
  studentCount: number;
  todoCount: number;
};

export enum OrderByType {
  ASC = 'ASC',
  DESC = 'DESC',
  REMAIN_DAYS_ASC = 'REMAIN_DAYS_ASC',
  REMAIN_DAYS_DESC = 'REMAIN_DAYS_DESC',
  /** 최신순 (내준날짜 최신 먼저) */
  LATEST = 'LATEST',
  /** 미완료순 (미완료 과제 먼저, 같은 상태 내에서는 최신순) */
  INCOMPLETE_FIRST = 'INCOMPLETE_FIRST',
}

export enum AssignmentKindType {
  TEST = 'TEST',
  ASSIGNMENT = 'ASSIGNMENT',
}

export type TxInfo = {
  /** TX pk */
  id: number;
  /** book/chapter */
  book: string;
  /** lesson */
  lesson: string;
  /** code */
  code: string;
  /** title */
  title: string;
  /** 할일type */
  type: AssignmentType;
  /** 과제 종류 */
  todoKind: AssignmentKindType;
  /** 진행상태 */
  toDoStatus: AssignmentStatusType;
  /** 시작일 */
  fromDate: string;
  /** 종료일 */
  toDate: string;
  /** link */
  link: string;
  /** 반 평균 점수 (테스트 과제만, 제출자 없으면 null) */
  avgScore?: number | null;
  /** 생성일 */
  createdAt: string;
  /** 수정일 */
  updatedAt: string;
  /** 삭제일 */
  deletedAt: string | null;
};

export type AssignmentStatusByClassItem = {
  txId: string;
  txInfo: TxInfo;
};

export type AssignmentStatusByStudentItem = {
  /** pk */
  id: number;
  /** 학생이름 */
  name: string;
  /** 학생 UID */
  uid: string;
  /** 누적할일수 */
  accumulatedTodoCount: number;
  /** 누적뱃지수 */
  accumulatedBadgeCount: number;
  /** 조회 필터 기준 배정 과제 수 (학생별보기 카드의 "과제 N개") */
  todoCount: number;
  /** 완료 과제 수 (학생별보기 카드의 "N개 완료") */
  completedCount: number;
  /** 완료율 0~100 (학생별보기 카드의 진도바) */
  progressRate: number;
  /** 소속 반 ID (학생별보기 카드의 반 배지. 반 미지정이면 null) */
  classGroupId: number | null;
  /** 소속 반 이름 (반 미지정이면 null) */
  classGroupName: string | null;
  /** 보유 달란트 합계 */
  dalantTotal: number;
  /** 생성일 */
  createdAt: string;
  /** 수정일 */
  updatedAt: string;
  /** 삭제일 */
  deletedAt: string | null;
};

export type TestItem = {
  seq: number;
  correct: boolean;
  selectedAnswers: number[];
  correctAnswers: number[];
  explanation: string | null;
  points: number;
};

export type AssignmentStatusProgress = {
  /** 배정된 할일 pk */
  id: number;
  /** link */
  link: string;
  /** 진행률 */
  progress: number;
  /** 진행상태 */
  progressState: AssignmentStatusType;
  /** 시작일 */
  fromDate: string;
  /** 종료일 */
  toDate: string;
  /** 완료일 */
  completedDate: string;
  /** 테스트 점수 (0~100). 테스트 과제가 아니거나 미제출이면 null */
  testScore?: number | null;
  /** 정오표 - 문항별 채점 결과 (테스트 과제 제출 시에만, 그 외 null) */
  testItems?: TestItem[] | null;
  /** 인증제 합격 날짜 */
  passDate: string;
  /** 인증제 실패 날짜 */
  failDate: string;
  /** 인증제 테스트 날짜 */
  testDate: string;
  /** 할일type */
  type: AssignmentType;
};

export type AssignmentStatusStudent = {
  /** 학생 pk */
  id: number;
  /** 유저 pk */
  user_id: string;
  /** 학생 이름 */
  name: string;
  /** 학생 uid */
  uid: string;
  /** 구젭스 id */
  legacyJebsId: string;
};

export type AssignmentStatusDetailByClassItem = {
  progress: AssignmentStatusProgress;
  student: AssignmentStatusStudent;
};

export type AssignmentStatusDetailByStudentItem = {
  /** pk */
  id: number;
  /** book/chapter */
  book: string;
  /** lesson */
  lesson: string;
  /** viewId */
  viewId: string;
  /** code */
  code: string;
  /** title */
  title: string;
  /** result(상세보기) */
  result: string;
  /** type */
  type: AssignmentType;
  /** 진행률 */
  progress: number;
  /** 진행상태 */
  progressState: AssignmentStatusType;
  /** 완료일 */
  completedDate: string;
  /** 시작일 */
  fromDate: string;
  /** 종료일 */
  toDate: string;
  /** 인증제 합격 날짜 */
  passDate: string;
  /** 인증제 실패 날짜 */
  failDate: string;
  /** 인증제 테스트 날짜 */
  testDate: string;
  /** 과제 종류 */
  todoKind: AssignmentKindType;
  testScore?: number | null;
  /** 정오표 - 문항별 채점 결과 (테스트 과제 제출 시에만, 그 외 null) */
  testItems?: TestItem[] | null;
  /** 생성일 */
  createdAt: string;
  /** 수정일 */
  updatedAt: string;
  /** 삭제일 */
  deletedAt: string | null;
};

export enum RecitationStatusType {
  TOTAL = 'TOTAL',
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  ANALYZING = 'ANALYZING',
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  ANALYSIS_FAILED = 'ANALYSIS_FAILED',
  INVALIDATED = 'INVALIDATED',
}

export enum RecitationOrderByType {
  ATTEMPTED_AT = 'ATTEMPTED_AT',
  STUDENT_NAME = 'STUDENT_NAME',
  LEVEL = 'LEVEL',
}

export type RecitationStatusItem = {
  studentId: number;
  studentName: string;
  studentUid: string;
  todoId: number;
  level: number;
  bookNo: number;
  bookTitle: string;
  resultUrl: string;
  attemptedAt: string;
  score: number;
  status: RecitationStatusType;
  txId: number;
  todoStatus: AssignmentStatusType;
  dueDate: string;
};

export type RecitationStatusSummary = {
  failed: number;
  others: number;
  passed: number;
  total: number;
};

export type RecitationStatusBookItem = {
  bestScore: number;
  bookNo: number;
  bookTitle: string;
  level: number;
  status: RecitationStatusType;
  attemptedAt: string;
};
